/**
 * The short transition: the screen itself slides off, and the next one slides on
 * behind it, travelling the same way.
 *
 * Two ideas hold it together, and both are about the app having a SHAPE rather
 * than a stack of screens.
 *
 * **Every screen lives in a direction from the main menu.** Play Locally is up,
 * Play Online is left, Table & Cards is right, Progress is down. Going there
 * pushes the menu that way; coming back retraces it. After a few minutes nobody
 * has to read that to know it — a Back tap from Progress feels like coming back
 * up because it is the same move in reverse, and the map is the same every time.
 *
 * **The motion is friction, not easing.** A sheet of something heavy does not
 * start smoothly: the force builds against static friction while nothing moves,
 * it breaks free all at once, and then kinetic friction is what brings the next
 * one to rest. That is a different curve from an ease-in-out in a way people
 * feel without naming — a long, almost-still lead-in, a sudden break, and a
 * settle that arrives rather than glides.
 *
 * Pure: the geometry and the timing are decided here, and `ScreenTransition`
 * only hands them to the Web Animations API.
 */

export type SlideDirection = "up" | "down" | "left" | "right";

/**
 * Where each screen sits relative to the main menu.
 *
 * Prefixes, so `/online/lobby/g_1` is in the same place as `/online` — a lobby
 * is somewhere inside Play Online, not a fifth direction. Anything not listed
 * has no place of its own and falls back to the travel rules below.
 */
const PLACES: readonly { prefix: string; direction: SlideDirection }[] = [
  { prefix: "/local/setup", direction: "up" },
  { prefix: "/online", direction: "left" },
  { prefix: "/themes", direction: "right" },
  { prefix: "/stats", direction: "down" },
];

/** Which way a screen is from the menu, or null for the menu itself and anything unplaced. */
export function placeOf(path: string): SlideDirection | null {
  const clean = (path.split("?")[0] ?? path).replace(/\/+$/, "") || "/";
  return PLACES.find((p) => clean === p.prefix || clean.startsWith(`${p.prefix}/`))?.direction ?? null;
}

export function opposite(direction: SlideDirection): SlideDirection {
  switch (direction) {
    case "up":
      return "down";
    case "down":
      return "up";
    case "left":
      return "right";
    case "right":
      return "left";
  }
}

/**
 * Which way this navigation travels.
 *
 * The rule is "go the way the destination lies, come back the way you went", and
 * the three cases fall out of that:
 *
 *  - heading somewhere with a place of its own — go that way;
 *  - heading back out of one — reverse the way that screen lies, so the trip
 *    retraces rather than picking a fresh direction;
 *  - neither end is placed — fall back on forward and back, which is all the
 *    navigation itself knows.
 *
 * `back` is what a retreat passes (`ScreenShell`'s button, a dialog going home),
 * and it wins over the destination: leaving Play Online for the menu is a
 * retreat whichever way the menu happens to be from there.
 */
export function slideFor(from: string, to: string, back = false): SlideDirection {
  const fromPlace = placeOf(from);
  const toPlace = placeOf(to);
  // A retreat reverses the way the screen being LEFT lies. The destination's
  // place is deliberately not consulted here: that says which way it is from the
  // menu, which is the outward trip, not the way home from it.
  if (back) return fromPlace ? opposite(fromPlace) : BACK_DEFAULT;
  if (toPlace) return toPlace;
  if (fromPlace) return opposite(fromPlace);
  return FORWARD_DEFAULT;
}

/**
 * Where an unplaced screen goes.
 *
 * Forward and back, and they are each other's reverse so that a trip out and
 * home still retraces even between two screens neither of which has a place on
 * the map.
 */
const FORWARD_DEFAULT: SlideDirection = "left";
const BACK_DEFAULT: SlideDirection = "right";

/** The axis a direction travels on, and which way along it the OUTGOING screen goes. */
export function axisOf(direction: SlideDirection): { axis: "X" | "Y"; sign: 1 | -1 } {
  switch (direction) {
    case "up":
      return { axis: "Y", sign: -1 };
    case "down":
      return { axis: "Y", sign: 1 };
    case "left":
      return { axis: "X", sign: -1 };
    case "right":
      return { axis: "X", sign: 1 };
  }
}

/**
 * How far into the stroke the screen breaks free, and how little of the distance
 * it has covered by then.
 *
 * This pair IS the static friction. For the first sixth of the time the screen
 * moves about two percent of the way — enough to see it straining and not enough
 * to read as travel — and then the remaining ninety-eight percent happens in
 * what is left. The jump in velocity at that moment is the break.
 *
 * Turn `CREEP` to 0 and it becomes a shove against something already loose;
 * turn `BREAK_AT` up and the app feels heavier and more reluctant.
 */
export const STICTION_BREAK_AT = 0.16;
export const STICTION_CREEP = 0.02;

/**
 * Loading up against the stop: slow, and still slow when it lets go.
 *
 * Ease-IN, so the segment ends at its fastest — which is what makes the break a
 * jump rather than a corner.
 */
const LOADING = "cubic-bezier(0.35, 0, 0.75, 0.4)";

/**
 * Broken free and accelerating away.
 *
 * It leaves at its FASTEST, and that is doing a job: the outgoing screen hands
 * over to the incoming one at the moment of the route swap, and two strokes that
 * both pass through that instant at speed read as one continuous movement. An
 * outgoing screen that eased to a halt on its way off would put a dead beat in
 * the middle of the trip.
 */
const BREAKING_AWAY = "cubic-bezier(0.4, 0.04, 0.72, 0.62)";

/**
 * Kinetic friction bringing the new screen to rest.
 *
 * Arrives fast, slows hard, stops dead. No overshoot and no bounce: friction
 * does not spring anything back, and a screen that settled past its mark and
 * returned would read as elastic rather than as heavy.
 */
const SETTLING = "cubic-bezier(0.12, 0.72, 0.22, 1)";

export interface SlideStroke {
  keyframes: Keyframe[];
  easing?: string;
}

/** The outgoing screen: loads against friction, breaks free, leaves at speed. */
export function outStroke(direction: SlideDirection): SlideStroke {
  const { axis, sign } = axisOf(direction);
  const at = (pct: number) => ({ transform: `translate${axis}(${(sign * pct).toFixed(3)}%)` });
  return {
    keyframes: [
      { ...at(0), offset: 0, easing: LOADING },
      { ...at(STICTION_CREEP * 100), offset: STICTION_BREAK_AT, easing: BREAKING_AWAY },
      { ...at(100), offset: 1 },
    ],
  };
}

/**
 * The incoming screen: already moving when it appears, friction stops it.
 *
 * It starts on the FAR side — the opposite edge from the one the old screen left
 * by — so the pair travel the same way rather than meeting in the middle. That is
 * the whole of "the direction of motion is the same".
 */
export function inStroke(direction: SlideDirection): SlideStroke {
  const { axis, sign } = axisOf(direction);
  const at = (pct: number) => ({ transform: `translate${axis}(${(sign * pct).toFixed(3)}%)` });
  return { keyframes: [{ ...at(-100) }, { ...at(0) }], easing: SETTLING };
}
